import { Routes } from '@angular/router';
import { ShellComponent } from './shared/shell/shell.component';
import { authRedirectGuard } from './core/guards/auth-redirect.guard';
import { authGuard } from './core/guards/auth.guard';
import { guideGuard } from './core/guards/guide.guard';
import { newExperienceGuard } from './core/guards/new-experience.guard';
import { settingsLandingGuard } from './core/guards/settings-landing.guard';
import { tenantAdminGuard } from './core/guards/tenant-admin.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { ScreenSize } from './services/screen-size.service';

// A child route without its own `data.sizes` takes its parent's; the shell shows the size message when the window's size is not listed.
const ALL_SIZES: ScreenSize[] = ['phone', 'tablet', 'computer'];
const TABLET_UP: ScreenSize[] = ['tablet', 'computer'];
const COMPUTER_ONLY: ScreenSize[] = ['computer'];

export const routes: Routes = [
  {
    path: 'login',
    title: 'titles.login',
    canActivate: [authRedirectGuard],
    loadComponent: () =>
      import('./pages/authentication/login/login.component').then(
        (m) => m.LoginComponent,
      ),
  },
  {
    path: 'signup',
    title: 'titles.signup',
    canActivate: [authRedirectGuard],
    loadComponent: () =>
      import('./pages/authentication/signup/signup.component').then(
        (m) => m.SignupComponent,
      ),
  },
  {
    path: 'forgot',
    title: 'titles.forgot',
    canActivate: [authRedirectGuard],
    loadComponent: () =>
      import('./pages/authentication/forgot/forgot.component').then(
        (m) => m.ForgotComponent,
      ),
  },
  {
    path: 'set-password',
    title: 'titles.setPassword',
    canActivate: [authRedirectGuard],
    loadComponent: () =>
      import('./pages/authentication/set-password/set-password.component').then(
        (m) => m.SetPasswordComponent,
      ),
  },
  // Links already e-mailed point at the old paths; the token query must survive the redirect.
  { path: 'forgot-password', redirectTo: 'forgot', pathMatch: 'full' },
  { path: 'reset-password', redirectTo: 'set-password', pathMatch: 'full' },
  {
    path: 'accept-invitation',
    title: 'titles.acceptInvitation',
    canActivate: [authRedirectGuard],
    loadComponent: () =>
      import('./pages/authentication/accept-invitation/accept-invitation.component').then(
        (m) => m.AcceptInvitationComponent,
      ),
  },
  {
    path: 'confirm-email-change',
    title: 'titles.confirmEmailChange',
    loadComponent: () =>
      import(
        './pages/authentication/confirm-email-change/confirm-email-change.component'
      ).then((m) => m.ConfirmEmailChangeComponent),
  },
  {
    // A task page for the person at the door: outside the shell, so no top bar, tab bar or footer, and on every size.
    path: 'welcome/:slotId',
    title: 'titles.welcome',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/welcome/welcome-page.component').then((m) => m.WelcomePageComponent),
  },
  {
    // The sheet for the printer: outside the shell, so no top bar, tab bar or footer ends up on paper.
    path: 'today/print',
    title: 'titles.todayPrint',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/today/print/today-print-page.component').then((m) => m.TodayPrintPageComponent),
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'today', pathMatch: 'full' },
      {
        path: 'today',
        data: { sizes: ALL_SIZES },
        title: 'titles.today',
        loadComponent: () =>
          import('./pages/today/today-page.component').then((m) => m.TodayPageComponent),
      },
      {
        path: 'start',
        data: { sizes: ALL_SIZES },
        title: 'titles.start',
        canActivate: [tenantAdminGuard],
        loadComponent: () => import('./pages/start/start-page.component').then((m) => m.StartPageComponent),
      },
      {
        path: 'guides',
        data: { sizes: ALL_SIZES },
        title: 'titles.guides',
        loadComponent: () => import('./pages/guides/guides-page.component').then((m) => m.GuidesPageComponent),
      },
      {
        path: 'guides/:slug',
        data: { sizes: ALL_SIZES },
        title: 'titles.guide',
        canActivate: [guideGuard],
        loadComponent: () => import('./pages/guides/guide-page.component').then((m) => m.GuidePageComponent),
      },
      {
        path: 'calendar',
        data: { sizes: TABLET_UP },
        title: 'titles.calendar',
        loadComponent: () =>
          import('./pages/calendar/calendar-page.component').then((m) => m.CalendarPageComponent),
      },
      {
        path: 'bookings',
        data: { sizes: TABLET_UP },
        title: 'titles.bookings',
        loadComponent: () =>
          import('./pages/bookings/bookings-page.component').then((m) => m.BookingsPageComponent),
      },
      // `new` must come before `:id`.
      {
        path: 'bookings/new',
        data: { sizes: TABLET_UP },
        title: 'titles.newBooking',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./pages/bookings/add-phone-booking/add-phone-booking-page.component').then(
            (m) => m.AddPhoneBookingPageComponent,
          ),
      },
      {
        path: 'bookings/:id',
        data: { sizes: ALL_SIZES },
        title: 'titles.booking',
        loadComponent: () =>
          import('./pages/bookings/booking-detail/booking-detail-page.component').then((m) => m.BookingDetailPageComponent),
      },
      {
        path: 'experiences',
        data: { sizes: TABLET_UP },
        title: 'titles.experiences',
        loadComponent: () =>
          import('./pages/experiences/experiences-page.component').then((m) => m.ExperiencesPageComponent),
      },
      {
        // `new` is an id the editor reads as "not created yet".
        path: 'experiences/:id',
        data: { sizes: TABLET_UP },
        canActivate: [newExperienceGuard],
        loadComponent: () =>
          import('./pages/experiences/experience-editor/experience-editor-layout.component').then(
            (m) => m.ExperienceEditorLayoutComponent,
          ),
        children: [
          { path: '', redirectTo: 'details', pathMatch: 'full' },
          {
            path: 'details',
            title: 'titles.experienceDetails',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/experiences/experience-editor/experience-details-page.component').then(
                (m) => m.ExperienceDetailsPageComponent,
              ),
          },
          {
            path: 'texts',
            title: 'titles.experienceTexts',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/experiences/experience-editor/experience-texts-page.component').then(
                (m) => m.ExperienceTextsPageComponent,
              ),
          },
          {
            path: 'schedule',
            title: 'titles.experienceSchedule',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/experiences/experience-editor/experience-schedule-page.component').then(
                (m) => m.ExperienceSchedulePageComponent,
              ),
          },
        ],
      },
      {
        path: 'guests',
        data: { sizes: TABLET_UP },
        title: 'titles.guests',
        loadComponent: () => import('./pages/guests/guests-page.component').then((m) => m.GuestsPageComponent),
      },
      {
        path: 'more',
        data: { sizes: TABLET_UP },
        title: 'titles.more',
        loadComponent: () =>
          import('./pages/more/more-page.component').then((m) => m.MorePageComponent),
      },
      {
        path: 'settings',
        data: { sizes: COMPUTER_ONLY },
        title: 'titles.settings',
        loadComponent: () =>
          import('./pages/settings/components/settings-layout.component').then((m) => m.SettingsLayoutComponent),
        children: [
          { path: '', pathMatch: 'full', canActivate: [settingsLandingGuard], children: [] },
          { path: 'profile', pathMatch: 'full', canActivate: [settingsLandingGuard], children: [] },
          {
            path: 'business',
            title: 'titles.settingsBusiness',
            canActivate: [tenantAdminGuard],
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/settings/business/business-settings-page.component').then(
                (m) => m.BusinessSettingsPageComponent,
              ),
          },
          {
            path: 'branding',
            title: 'titles.settingsBranding',
            canActivate: [tenantAdminGuard],
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/settings/branding/branding-settings-page.component').then(
                (m) => m.BrandingSettingsPageComponent,
              ),
          },
          {
            path: 'booking',
            title: 'titles.settingsBooking',
            canActivate: [tenantAdminGuard],
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/settings/booking/booking-settings-page.component').then(
                (m) => m.BookingSettingsPageComponent,
              ),
          },
          {
            path: 'emails',
            title: 'titles.settingsEmails',
            canActivate: [tenantAdminGuard],
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/settings/emails/email-settings-page.component').then(
                (m) => m.EmailSettingsPageComponent,
              ),
          },
          {
            path: 'website',
            title: 'titles.settingsWebsite',
            canActivate: [tenantAdminGuard],
            loadComponent: () =>
              import('./pages/settings/website/website-settings-page.component').then(
                (m) => m.WebsiteSettingsPageComponent,
              ),
          },
          {
            path: 'plan',
            title: 'titles.settingsPlan',
            canActivate: [tenantAdminGuard],
            loadComponent: () =>
              import('./pages/settings/plan/plan-settings-page.component').then(
                (m) => m.PlanSettingsPageComponent,
              ),
          },
          {
            path: 'account',
            title: 'titles.settingsAccount',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./pages/settings/account/account-settings-page.component').then(
                (m) => m.AccountSettingsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'forbidden',
        data: { sizes: ALL_SIZES },
        title: 'titles.forbidden',
        loadComponent: () =>
          import('./pages/forbidden/forbidden-page.component').then(
            (m) => m.ForbiddenPageComponent,
          ),
      },
      {
        path: 'not-found',
        data: { sizes: ALL_SIZES },
        title: 'titles.notFound',
        loadComponent: () =>
          import('./pages/not-found-page.component').then(
            (m) => m.NotFoundPageComponent,
          ),
      },
      {
        path: '**',
        redirectTo: '/not-found',
      },
    ],
  },
];
